import React, { useState, useEffect } from 'react';
import { X, Send, Users, Plus, Trash2, MessageSquare } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { SEND_WHATSAPP_OFFER, SEND_WHATSAPP_BULK } from '../../graphql/mutations/whatsapp';
import type { WhatsAppSendOfferInput, WhatsAppRecipient, WhatsAppTemplate } from '../../types/whatsapp';

interface WhatsAppSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefillPackage?: {
    id: number;
    title: string;
    destination: string;
    price?: string;
    departureDate?: string;
    image?: string;
  };
  templates: WhatsAppTemplate[];
  onSent?: () => void;
}

type SendMode = 'single' | 'bulk';

const emptyOffer: WhatsAppSendOfferInput = {
  recipientPhone: '',
  recipientName: '',
  title: '',
  destination: '',
  price: '',
  departureDate: '',
  imageUrl: '',
  bookingUrl: '',
  customMessage: '',
};

export default function WhatsAppSendModal({ isOpen, onClose, prefillPackage, templates, onSent }: WhatsAppSendModalProps) {
  const [mode, setMode] = useState<SendMode>('single');
  const [form, setForm] = useState<WhatsAppSendOfferInput>(emptyOffer);
  const [recipients, setRecipients] = useState<WhatsAppRecipient[]>([{ phone: '', name: '' }]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [results, setResults] = useState<{ phone: string; success: boolean; error?: string }[]>([]);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (prefillPackage) {
      setForm(prev => ({
        ...prev,
        packageId: prefillPackage.id,
        title: prefillPackage.title,
        destination: prefillPackage.destination,
        price: prefillPackage.price || '',
        departureDate: prefillPackage.departureDate || '',
        imageUrl: prefillPackage.image || '',
      }));
    }
  }, [prefillPackage]);

  const [sendOffer, { loading: sendingOne }] = useMutation(SEND_WHATSAPP_OFFER, {
    onCompleted: (data) => {
      const res = data.sendWhatsAppOffer;
      if (res.success) {
        setResults([{ phone: form.recipientPhone, success: true }]);
        onSent?.();
      } else {
        setResults([{ phone: form.recipientPhone, success: false, error: res.error }]);
      }
    },
    onError: (err) => setError(err.message),
  });

  const [sendBulk, { loading: sendingBulk }] = useMutation(SEND_WHATSAPP_BULK, {
    onCompleted: (data) => {
      const res: { success: boolean; error?: string }[] = data.sendWhatsAppBulk;
      setResults(res.map((r, i) => ({ phone: recipients[i]?.phone || '', success: r.success, error: r.error })));
      onSent?.();
    },
    onError: (err) => setError(err.message),
  });

  const loading = sendingOne || sendingBulk;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const applyTemplate = (templateId: number) => {
    const t = templates.find(t => t.id === templateId);
    if (!t) return;
    setForm(prev => ({ ...prev, customMessage: t.content }));
    setSelectedTemplate(templateId);
  };

  const addRecipient = () => setRecipients(prev => [...prev, { phone: '', name: '' }]);
  const removeRecipient = (i: number) => setRecipients(prev => prev.filter((_, idx) => idx !== i));
  const updateRecipient = (i: number, field: 'phone' | 'name', value: string) => {
    setRecipients(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults([]);

    if (!form.title || !form.destination) {
      setError('Titre et destination sont requis');
      return;
    }

    if (mode === 'single') {
      if (!form.recipientPhone) { setError('Numéro du destinataire requis'); return; }
      sendOffer({ variables: { input: { ...form, packageId: form.packageId || undefined } } });
    } else {
      const validRecipients = recipients.filter(r => r.phone.trim());
      if (!validRecipients.length) { setError('Ajoutez au moins un destinataire'); return; }
      sendBulk({
        variables: {
          input: {
            recipients: validRecipients,
            packageId: form.packageId || undefined,
            title: form.title,
            destination: form.destination,
            price: form.price || undefined,
            departureDate: form.departureDate || undefined,
            imageUrl: form.imageUrl || undefined,
            bookingUrl: form.bookingUrl || undefined,
          },
        },
      });
    }
  };

  const buildPreviewText = () => {
    if (form.customMessage) return form.customMessage;
    const lines = [
      `✈️ *${form.title || 'Titre du voyage'}*`,
      '',
      `📍 Destination : *${form.destination || 'Destination'}*`,
      form.departureDate ? `📅 Départ : *${form.departureDate}*` : null,
      form.price ? `💰 À partir de *${form.price}*` : null,
      '',
      form.bookingUrl ? `🔗 Réserver : ${form.bookingUrl}` : null,
    ].filter(Boolean).join('\n');
    return lines;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Envoyer une offre WhatsApp</h2>
              <p className="text-sm text-gray-500">Partagez un voyage avec vos clients</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          {(['single', 'bulk'] as SendMode[]).map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); setResults([]); }}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                mode === m ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {m === 'single' ? <><Send className="w-4 h-4" /> Envoi unique</> : <><Users className="w-4 h-4" /> Envoi groupé</>}
            </button>
          ))}
        </div>

        {/* Résultats */}
        {results.length > 0 && (
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Résultats : {results.filter(r => r.success).length}/{results.length} envoyés
            </p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {results.map((r, i) => (
                <div key={i} className={`text-xs flex items-center gap-2 ${r.success ? 'text-green-700' : 'text-red-600'}`}>
                  <span>{r.success ? '✅' : '❌'}</span>
                  <span>{r.phone}</span>
                  {r.error && <span className="text-gray-500">— {r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          {/* Destinataires */}
          {mode === 'single' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone <span className="text-red-500">*</span></label>
                <input
                  name="recipientPhone"
                  value={form.recipientPhone}
                  onChange={handleChange}
                  placeholder="+213 XX XX XX XX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom client</label>
                <input
                  name="recipientName"
                  value={form.recipientName}
                  onChange={handleChange}
                  placeholder="Nom optionnel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Destinataires ({recipients.filter(r => r.phone).length})</label>
                <button type="button" onClick={addRecipient} className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
              </div>
              <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                {recipients.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={r.phone}
                      onChange={e => updateRecipient(i, 'phone', e.target.value)}
                      placeholder="+213 XX XX XX XX"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <input
                      value={r.name || ''}
                      onChange={e => updateRecipient(i, 'name', e.target.value)}
                      placeholder="Nom"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {recipients.length > 1 && (
                      <button type="button" onClick={() => removeRecipient(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <hr className="border-gray-200" />

          {/* Détails offre */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre <span className="text-red-500">*</span></label>
              <input name="title" value={form.title} onChange={handleChange} placeholder="Voyage à Istanbul" required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination <span className="text-red-500">*</span></label>
              <input name="destination" value={form.destination} onChange={handleChange} placeholder="Istanbul, Turquie" required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix</label>
              <input name="price" value={form.price} onChange={handleChange} placeholder="85 000 DZD"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date départ</label>
              <input name="departureDate" value={form.departureDate} onChange={handleChange} placeholder="15 juin 2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL image</label>
            <input name="imageUrl" value={form.imageUrl} onChange={handleChange} placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lien de réservation</label>
            <input name="bookingUrl" value={form.bookingUrl} onChange={handleChange} placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>

          {/* Templates */}
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <MessageSquare className="w-4 h-4" /> Message personnalisé (template)
              </label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applyTemplate(t.id)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      selectedTemplate === t.id
                        ? 'bg-green-100 border-green-400 text-green-700'
                        : 'border-gray-300 text-gray-600 hover:border-green-400'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Message personnalisé</label>
              <button
                type="button"
                onClick={() => setPreview(v => !v)}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {preview ? 'Masquer aperçu' : 'Aperçu'}
              </button>
            </div>
            <textarea
              name="customMessage"
              value={form.customMessage}
              onChange={handleChange}
              placeholder="Laissez vide pour utiliser le format automatique..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
            {preview && (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Aperçu du message :</p>
                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans">{buildPreviewText()}</pre>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
            Fermer
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading
              ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Envoi...</>
              : <><Send className="w-4 h-4" /> {mode === 'bulk' ? `Envoyer (${recipients.filter(r => r.phone).length})` : 'Envoyer'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
